import keys from 'lodash/keys';
import last from 'lodash/last';
import sortBy from 'lodash/sortBy';
import takeWhile from 'lodash/takeWhile';
import toPairs from 'lodash/toPairs';
import React, { useState, useEffect, useMemo } from 'react';
import {
  differenceInMilliseconds,
  formatTimeLeftRounded,
} from 'src/general/utils/dateLib';
import { useTranslation } from 'src/lib/translations';
import { Flex, FlexProps, HeadingProps, TextProps } from 'src/theme';

import { TIME_LABEL } from './constants';
import { TimeBlock } from './TimeBlock';

export interface CountdownProps extends FlexProps {
  date: string;
  labelProps?: TextProps;
  numberProps?: HeadingProps | { [key: number]: HeadingProps };
  textSize?: 'xs' | 'sm' | 'md' | 'lg';
}

export const Countdown = ({
  date,
  textSize,
  numberProps,
  ...rest
}: CountdownProps) => {
  const { t } = useTranslation();
  const millisecondsLeft = differenceInMilliseconds(new Date(date), Date.now());
  const [timeLeft, setTimeLeft] = useState(
    formatTimeLeftRounded(millisecondsLeft),
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(formatTimeLeftRounded(millisecondsLeft));
    }, 1000);
    return () => clearTimeout(timer);
  });

  // only sort and do this work if the numberProps change
  // rather than on every tick of the timer.
  const sortedPropBreakpoints = useMemo(() => {
    if (isNaN(Number(last(keys(numberProps))))) return null;
    return sortBy(toPairs(numberProps), [0]);
  }, [numberProps]);

  // if sortedPropBreakpoints exists, we have some math to do.
  // find the last entry in the array that is still below the defined
  // millis breakpoint, and choose the second value.
  const parsedNumberProps = sortedPropBreakpoints
    ? last(
        takeWhile(
          sortedPropBreakpoints,
          ([ms]) => Number(ms) <= millisecondsLeft,
        ),
      )?.[1]
    : numberProps;

  return (
    <Flex {...rest} data-testid='countdown'>
      <TimeBlock
        label={TIME_LABEL.DAY}
        number={timeLeft[TIME_LABEL.DAY]}
        numberProps={parsedNumberProps}
        textSize={textSize}
        unit={t('Countdown.days')}
      />
      <TimeBlock
        label={TIME_LABEL.HOUR}
        number={timeLeft[TIME_LABEL.HOUR]}
        numberProps={parsedNumberProps}
        textSize={textSize}
        unit={t('Countdown.hours')}
      />
      <TimeBlock
        label={TIME_LABEL.MINUTE}
        number={timeLeft[TIME_LABEL.MINUTE]}
        numberProps={parsedNumberProps}
        textSize={textSize}
        unit={t('Countdown.minutes')}
      />
      <TimeBlock
        label={TIME_LABEL.SECOND}
        number={timeLeft[TIME_LABEL.SECOND]}
        numberProps={parsedNumberProps}
        textSize={textSize}
        unit={t('Countdown.seconds')}
      />
    </Flex>
  );
};
